// src/protocols/vnoski.js — протокол от ОС за допълнителни парични вноски по
// чл. 134 ТЗ (внасяне на оборотни средства). Само за ООД.
//
// Съдружниците се предпопълват от „Мои фирми → Собственици" (име, ЕГН, дял),
// но остават редактируеми (кой е присъствал/гласувал може да е различно).
// Валидациите са на сървъра — грешките се показват като съобщение.

import { el, clear, $, field, input, row, clickableRow } from '../shared/dom.js';
import { numberLine } from '../shared/docNumber.js';
import { dateInput, localTodayIso, guardDates } from '../shared/dates.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, docNo } from '../shared/ui.js';
import { regHead, regCount, REG_TABLE } from '../shared/register.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { auditBlock } from '../shared/audit.js';
import { state, cq, activeCompanyObj } from '../shared/state.js';
import { legalFormOf } from '../shared/legal.js';

function r2(v) { return Math.round((Number(v) || 0) * 100) / 100; }
// Съразмерно разпределение по дялове; последният поема остатъка (както сървъра).
function distribute(total, shares) {
  const t = r2(total); const arr = shares.map((x) => Number(x) || 0);
  const sum = arr.reduce((s, p) => s + p, 0);
  if (!(t > 0) || !arr.length) return arr.map(() => 0);
  let rem = t;
  return arr.map((pct, i) => { if (i === arr.length - 1) return r2(rem); const part = sum > 0 ? r2(t * pct / sum) : r2(t / arr.length); rem = r2(rem - part); return part; });
}

export async function view(host) {
  clear(host);
  if (!state().activeCompany) return window.CRM.needsCompany(host);
  let list = [];
  try { list = await api('/vnoski' + cq()); } catch (e) { toast(e.message, true); }
  const panel = el('div', { class: 'panel' });
  // Видът е като в „Доставки" (Макс, 29.10.2026): бутонът на реда със заглавието, броячът над таблицата.
  panel.appendChild(regHead('Протоколи за допълнителни вноски (чл. 134 ТЗ)', [], el('button', { class: 'btn sm', type: 'button', text: '+ Нов протокол', onClick: () => vnoskiForm(null) })));
  if (!list.length) { panel.appendChild(emptyState('₪', 'Още няма протоколи за допълнителни парични вноски.', '+ Нов протокол', () => vnoskiForm(null))); host.appendChild(panel); return; }
  const tb = el('tbody');
  list.slice().sort((a, b) => (b.seq || 0) - (a.seq || 0)).forEach((v) => {
    tb.appendChild(clickableRow(() => vnoskiForm(v), 'Отвори № ' + (v.number || ''),
      el('td', { style: 'font-weight:600' }, '№ ', docNo(v.number)),
      el('td', {}, dfmt(v.protocol_date)),
      el('td', {}, v.pravna_forma === 'ЕООД' ? 'Решение (ЕООД)' : 'Протокол (ООД)'),
      el('td', { class: 'num' }, money(r2(v.obshta_suma), v.currency || 'EUR'))
    ));
  });
  const countLbl = regCount(); countLbl.set(list.length, list.length); panel.appendChild(countLbl);
  panel.appendChild(el('table', { class: REG_TABLE }, el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', text: 'Вид' }), el('th', { scope: 'col', class: 'num', text: 'Обща сума' }))), tb));
  host.appendChild(panel);
}

// Единен вход: избира макета по правната форма (записа, иначе фирмата).
function vnoskiForm(existing) {
  const v = existing || {};
  const company = (v.company_id && (state().companies || []).find((c) => Number(c.id) === Number(v.company_id))) || activeCompanyObj() || {};
  const form = (existing && existing.pravna_forma) ? existing.pravna_forma : (legalFormOf(company) || 'ООД');
  return form === 'ЕООД' ? eoodForm(existing, company) : oodForm(existing, company);
}

// Предупреждение (без блокиране) при разминаване форма ↔ брой собственици.
function mismatchNote(form, company) {
  const owners = Array.isArray(company.shareholders) ? company.shareholders.length : 0;
  let msg = '';
  if (form === 'ЕООД' && owners >= 2) msg = 'Фирмата е зададена като ЕООД, а има ' + owners + ' собственици. Проверете правната форма в „Мои фирми".';
  else if (form === 'ООД' && owners === 1) msg = 'Фирмата е зададена като ООД, а има само един собственик. Ако е ЕООД, сменете формата в „Мои фирми".';
  if (!msg) return null;
  return el('div', { style: 'margin:0 0 10px;padding:8px 10px;border-radius:6px;background:#fff7ed;border:1px solid #fdba74;color:#9a3412;font-size:13px', text: '⚠ ' + msg });
}

function oodForm(existing, companyArg) {
  const v = existing || {};
  const isAdmin = state().user && state().user.role === 'admin';
  const company = companyArg || (v.company_id && (state().companies || []).find((c) => Number(c.id) === Number(v.company_id))) || activeCompanyObj() || {};

  // Съдружници: от записа, иначе предпопълнени от собствениците на фирмата.
  let sadr = (v.sadruzhnitsi && v.sadruzhnitsi.length)
    ? v.sadruzhnitsi.map((s) => ({ ime: s.ime || '', egn: s.egn || '', dyal_procent: s.dyal_procent != null ? String(s.dyal_procent) : '' }))
    : (Array.isArray(company.shareholders) ? company.shareholders : []).map((s) => ({ ime: s.name || '', egn: s.egn || '', dyal_procent: s.share_percent != null ? String(s.share_percent) : '' }));
  if (!sadr.length) sadr = [{ ime: '', egn: '', dyal_procent: '' }];
  let vnoski = (v.vnoski_po_sadruzhnik && v.vnoski_po_sadruzhnik.length)
    ? v.vnoski_po_sadruzhnik.map((x) => ({ ime: x.ime || '', suma: x.suma != null ? String(x.suma) : '' }))
    : sadr.map((s) => ({ ime: s.ime, suma: '' }));

  const f = {
    protocol_date: dateInput(v.protocol_date || localTodayIso()),
    grad: input({ value: v.grad || company.city || 'София' }),
    svikano_ot: input({ value: v.svikano_ot || 'управителя' }),
    predstaven_kapital: input({ value: v.predstaven_kapital || '100%' }),
    obshta_suma: input({ type: 'number', step: '0.01', value: v.obshta_suma != null ? v.obshta_suma : '' }),
    razpredelenie: el('select', {},
      el('option', { value: 'съразмерно на дяловете в капитала', text: 'съразмерно на дяловете в капитала', selected: (v.razpredelenie || 'съразмерно на дяловете в капитала') === 'съразмерно на дяловете в капитала' }),
      el('option', { value: 'по друго съотношение (посочено в решението)', text: 'по друго съотношение (посочено в решението)', selected: v.razpredelenie === 'по друго съотношение (посочено в решението)' })),
    srok_vnasyane: dateInput(v.srok_vnasyane || ''),
    smetka: input({ value: v.smetka || company.iban || '' }),
    lihva_procent: input({ type: 'number', step: '0.01', value: v.lihva_procent != null ? v.lihva_procent : '' }),
    srok_vrashtane: input({ value: v.srok_vrashtane || '', placeholder: 'напр. след отпадане на необходимостта, по решение на ОС' }),
    glasuvali_za: input({ type: 'number', step: '0.01', value: v.glasuvali_za != null ? v.glasuvali_za : 100 }),
    glasuvali_protiv: input({ type: 'number', step: '0.01', value: v.glasuvali_protiv != null ? v.glasuvali_protiv : 0 }),
    glasuvali_vazdarzhal: input({ type: 'number', step: '0.01', value: v.glasuvali_vazdarzhal != null ? v.glasuvali_vazdarzhal : 0 }),
    predsedatel: input({ value: v.predsedatel || company.mol || '' }),
    protokolchik: input({ value: v.protokolchik || '' }),
  };

  // Условна лихва: полето за процент се показва само при включен флаг.
  const lihvaFlag = el('input', { type: 'checkbox', checked: !!v.lihva_flag });
  const lihvaWrap = field('Годишен лихвен процент (%)', f.lihva_procent);
  function syncLihva() { lihvaWrap.style.display = lihvaFlag.checked ? '' : 'none'; }
  lihvaFlag.addEventListener('change', syncLihva); syncLihva();

  // ---- Съдружници ----
  const sadrWrap = el('div');
  function drawSadr() {
    clear(sadrWrap);
    sadr.forEach((s, i) => {
      const nameI = input({ value: s.ime, placeholder: 'Име и фамилия' });
      nameI.addEventListener('input', (e) => { s.ime = e.target.value; });
      const egnI = input({ value: s.egn, placeholder: 'ЕГН', maxlength: '10', inputmode: 'numeric' });
      egnI.addEventListener('input', (e) => { s.egn = e.target.value; });
      const pctI = input({ value: s.dyal_procent, placeholder: '%', type: 'number', min: '0', max: '100', step: '0.01', style: 'max-width:90px' });
      pctI.addEventListener('input', (e) => { s.dyal_procent = e.target.value; });
      sadrWrap.appendChild(el('div', { class: 'field-row', style: 'align-items:flex-end' },
        field('Съдружник', nameI), field('ЕГН', egnI), field('Дял %', pctI),
        el('button', { class: 'btn sm danger', type: 'button', text: '×', style: 'margin-bottom:2px', onClick: () => { sadr.splice(i, 1); if (!sadr.length) sadr.push({ ime: '', egn: '', dyal_procent: '' }); drawSadr(); } })));
    });
    sadrWrap.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави съдружник', onClick: () => { sadr.push({ ime: '', egn: '', dyal_procent: '' }); drawSadr(); } }));
  }
  drawSadr();

  // ---- Вноски по съдружник + жива сума ----
  const sumBox = el('div', { style: 'margin-top:6px;font-weight:600' });
  const vnoskiWrap = el('div');
  function recompute() {
    const total = r2(f.obshta_suma.value);
    const sum = vnoski.reduce((s, x) => s + (Number(x.suma) || 0), 0);
    clear(sumBox);
    const ok = Math.abs(r2(sum) - total) < 0.005 && total > 0;
    sumBox.appendChild(el('span', { text: 'Сбор на вноските: ' + money(r2(sum), 'EUR') + ' / обща сума: ' + money(total, 'EUR') + (ok ? '  ✓' : '  ✗ не съвпадат'), style: 'color:' + (ok ? 'var(--ok,#0a7)' : 'var(--danger,#c33)') }));
  }
  function drawVnoski() {
    clear(vnoskiWrap);
    vnoski.forEach((x, i) => {
      const nameI = input({ value: x.ime, placeholder: 'Съдружник' });
      nameI.addEventListener('input', (e) => { x.ime = e.target.value; });
      const sumI = input({ type: 'number', step: '0.01', value: x.suma, placeholder: 'Сума (EUR)' });
      sumI.addEventListener('input', (e) => { x.suma = e.target.value; recompute(); });
      vnoskiWrap.appendChild(el('div', { class: 'field-row', style: 'align-items:flex-end' },
        field('Съдружник', nameI), field('Сума (EUR)', sumI),
        el('button', { class: 'btn sm danger', type: 'button', text: '×', style: 'margin-bottom:2px', onClick: () => { vnoski.splice(i, 1); if (!vnoski.length) vnoski.push({ ime: '', suma: '' }); drawVnoski(); recompute(); } })));
    });
    vnoskiWrap.appendChild(el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin-top:4px' },
      el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави ред', onClick: () => { vnoski.push({ ime: '', suma: '' }); drawVnoski(); } }),
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Разпредели по дялове', onClick: () => {
        vnoski = sadr.filter((s) => (s.ime || '').trim()).map((s) => ({ ime: s.ime, dyal: s.dyal_procent }));
        const sums = distribute(f.obshta_suma.value, vnoski.map((x) => x.dyal));
        vnoski = vnoski.map((x, i) => ({ ime: x.ime, suma: String(sums[i]) }));
        drawVnoski(); recompute();
      } })));
  }
  drawVnoski();
  f.obshta_suma.addEventListener('input', recompute);
  recompute();

  const body = el('div', {},
    numberLine('/vnoski', 'Новият протокол', existing),
    mismatchNote('ООД', company),
    row(field('Дата на заседанието', f.protocol_date), field('Населено място', f.grad)),
    row(field('Свикано от', f.svikano_ot), field('Представен капитал', f.predstaven_kapital)),
    el('h4', { text: 'Присъстващи съдружници', style: 'margin:12px 0 4px' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'Предпопълнени от „Мои фирми → Собственици" — редактирайте кой присъства. ЕГН е чувствително и се показва маскирано на не-администратори.' }),
    sadrWrap,
    el('h4', { text: 'Размер и разпределение', style: 'margin:12px 0 4px' }),
    row(field('Обща сума на вноските (EUR)', f.obshta_suma), field('Разпределение', f.razpredelenie)),
    vnoskiWrap, sumBox,
    el('h4', { text: 'Условия', style: 'margin:12px 0 4px' }),
    row(field('Срок за внасяне', f.srok_vnasyane), field('Банкова сметка (IBAN)', f.smetka)),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, lihvaFlag, el('span', { text: 'Дружеството плаща лихва върху вноските (чл. 134, ал. 3)' }))),
    lihvaWrap,
    field('Срок / условия за връщане', f.srok_vrashtane),
    el('h4', { text: 'Гласуване (% от капитала)', style: 'margin:12px 0 4px' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'Решението изисква мнозинство над 3/4 (над 75%) от капитала — чл. 137, ал. 3 ТЗ.' }),
    row(field('„За"', f.glasuvali_za), field('„Против"', f.glasuvali_protiv), field('„Въздържал се"', f.glasuvali_vazdarzhal)),
    row(field('Председател', f.predsedatel), field('Протоколчик', f.protokolchik))
  );
  if (existing && existing.id) { const _ab = auditBlock(existing); if (_ab) body.appendChild(_ab); }

  const save = async () => {
    // Неразбрана дата не тръгва празна — сървърът би сложил днешна (ПРОМТ-19).
    if (!guardDates(f.protocol_date)) return;
    const p = {
      company_id: v.company_id || state().activeCompany,
      pravna_forma: 'ООД',
      protocol_date: f.protocol_date.dataset.iso, grad: f.grad.value,
      svikano_ot: f.svikano_ot.value, predstaven_kapital: f.predstaven_kapital.value,
      sadruzhnitsi: sadr.filter((s) => (s.ime || '').trim()).map((s) => ({ ime: s.ime.trim(), egn: String(s.egn || '').trim(), dyal_procent: s.dyal_procent === '' ? null : Number(s.dyal_procent) })),
      obshta_suma: f.obshta_suma.value, razpredelenie: f.razpredelenie.value,
      vnoski_po_sadruzhnik: vnoski.filter((x) => (x.ime || '').trim()).map((x) => ({ ime: x.ime.trim(), suma: Number(x.suma) || 0 })),
      srok_vnasyane: f.srok_vnasyane.dataset.iso, smetka: f.smetka.value,
      lihva_flag: lihvaFlag.checked, lihva_procent: lihvaFlag.checked ? (f.lihva_procent.value === '' ? null : Number(f.lihva_procent.value)) : null,
      srok_vrashtane: f.srok_vrashtane.value,
      glasuvali_za: f.glasuvali_za.value, glasuvali_protiv: f.glasuvali_protiv.value, glasuvali_vazdarzhal: f.glasuvali_vazdarzhal.value,
      predsedatel: f.predsedatel.value, protokolchik: f.protokolchik.value,
    };
    try {
      if (existing && existing.id) await api('/vnoski/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/vnoski', { method: 'POST', body: JSON.stringify(p) });
      toast('Записано.'); closeModal(); window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); }
  };

  const foot = el('div', { style: 'flex-wrap:wrap' });
  if (existing && existing.id) {
    // „Създай подобен": нов протокол със същите съдружници, суми и условия.
    // Без id/номер (броячът дава номера при „Запази"). Датата става днешна, а
    // срокът за внасяне НЕ се пренася — той е на старото решение и е
    // задължителен реквизит, тоест валидацията ще поиска нов. Маскирано ЕГН
    // (не-админ) се допопълва от сървъра по име при записа (enrichEgn).
    foot.appendChild(el('button', { class: 'btn sm ghost copy-btn', text: 'Създай подобен', title: 'Нов протокол със същите съдружници и условия', style: 'margin-right:auto', onClick: () => {
      closeModal();
      oodForm({
        company_id: v.company_id,
        pravna_forma: 'ООД',
        grad: v.grad,
        svikano_ot: v.svikano_ot,
        predstaven_kapital: v.predstaven_kapital,
        sadruzhnitsi: v.sadruzhnitsi,
        obshta_suma: v.obshta_suma,
        razpredelenie: v.razpredelenie,
        vnoski_po_sadruzhnik: v.vnoski_po_sadruzhnik,
        smetka: v.smetka,
        lihva_flag: v.lihva_flag,
        lihva_procent: v.lihva_procent,
        srok_vrashtane: v.srok_vrashtane,
        glasuvali_za: v.glasuvali_za,
        glasuvali_protiv: v.glasuvali_protiv,
        glasuvali_vazdarzhal: v.glasuvali_vazdarzhal,
        predsedatel: v.predsedatel,
        protokolchik: v.protokolchik,
      }, company);
    } }));
    foot.appendChild(el('button', { class: 'btn sm ghost', text: 'PDF', onClick: () => window.open('/api/vnoski/' + existing.id + '/pdf', '_blank') }));
    if (isAdmin) foot.appendChild(el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (!(await confirmDialog('Да изтрия ли този протокол?', { okText: 'Изтрий', danger: true }))) return; try { await api('/vnoski/' + existing.id, { method: 'DELETE' }); toast('Изтрито.'); closeModal(); window.CRM.renderView($('#page')); } catch (e) { toast(e.message, true); } } }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', text: 'Запази', onClick: save, style: 'margin-left:8px' }));
  openModal({ title: (existing && existing.id) ? ('Протокол за допълнителни вноски № ' + existing.number) : 'Нов протокол за допълнителни вноски (чл. 134 ТЗ)', body, foot, wide: true });
}

// ЕООД: решение на едноличния собственик (чл. 147, ал. 2 във вр. с чл. 134).
// Няма съдружници/гласуване/разпределение — само едноличен собственик.
function eoodForm(existing, companyArg) {
  const v = existing || {};
  const isAdmin = state().user && state().user.role === 'admin';
  const company = companyArg || activeCompanyObj() || {};
  // Предпопълва собственика от „Мои фирми → Собственици" (първия).
  const owner0 = (Array.isArray(company.shareholders) && company.shareholders[0]) || {};

  const f = {
    protocol_date: dateInput(v.protocol_date || localTodayIso()),
    grad: input({ value: v.grad || company.city || 'София' }),
    sobstvenik_ime: input({ value: v.sobstvenik_ime || owner0.name || company.mol || '' }),
    sobstvenik_egn: input({ value: v.sobstvenik_egn || owner0.egn || '', placeholder: 'ЕГН', maxlength: '10', inputmode: 'numeric' }),
    sobstvenik_eik: input({ value: v.sobstvenik_eik || '', placeholder: 'ЕИК на собственика' }),
    predstavlyavasht: input({ value: v.predstavlyavasht || '', placeholder: 'Име на представляващия' }),
    obshta_suma: input({ type: 'number', step: '0.01', value: v.obshta_suma != null ? v.obshta_suma : '' }),
    srok_vnasyane: dateInput(v.srok_vnasyane || ''),
    smetka: input({ value: v.smetka || company.iban || '' }),
    lihva_procent: input({ type: 'number', step: '0.01', value: v.lihva_procent != null ? v.lihva_procent : '' }),
    srok_vrashtane: input({ value: v.srok_vrashtane || '', placeholder: 'напр. след отпадане на необходимостта, по решение на собственика' }),
  };

  // Юридическо лице → ЕИК + представляващ вместо ЕГН.
  const isLegal = el('input', { type: 'checkbox', checked: !!v.sobstvenik_is_legal });
  const egnWrap = field('ЕГН на собственика', f.sobstvenik_egn);
  const eikWrap = field('ЕИК на собственика', f.sobstvenik_eik);
  const predWrap = field('Представляващ', f.predstavlyavasht);
  function syncLegal() {
    egnWrap.style.display = isLegal.checked ? 'none' : '';
    eikWrap.style.display = isLegal.checked ? '' : 'none';
    predWrap.style.display = isLegal.checked ? '' : 'none';
  }
  isLegal.addEventListener('change', syncLegal); syncLegal();

  // Условна лихва.
  const lihvaFlag = el('input', { type: 'checkbox', checked: !!v.lihva_flag });
  const lihvaWrap = field('Годишен лихвен процент (%)', f.lihva_procent);
  function syncLihva() { lihvaWrap.style.display = lihvaFlag.checked ? '' : 'none'; }
  lihvaFlag.addEventListener('change', syncLihva); syncLihva();

  const body = el('div', {},
    numberLine('/vnoski', 'Новото решение', existing),
    mismatchNote('ЕООД', company),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'Решение на едноличния собственик (чл. 147, ал. 2 във връзка с чл. 134 ТЗ) — без общо събрание, гласуване и разпределение по съдружници.' }),
    row(field('Дата на решението', f.protocol_date), field('Населено място', f.grad)),
    el('h4', { text: 'Едноличен собственик', style: 'margin:12px 0 4px' }),
    field('Едноличен собственик (име)', f.sobstvenik_ime),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, isLegal, el('span', { text: 'Собственикът е юридическо лице (ЕИК + представляващ)' }))),
    egnWrap, eikWrap, predWrap,
    el('h4', { text: 'Вноска', style: 'margin:12px 0 4px' }),
    row(field('Размер на вноската (EUR)', f.obshta_suma), field('Срок за внасяне', f.srok_vnasyane)),
    field('Банкова сметка (IBAN)', f.smetka),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, lihvaFlag, el('span', { text: 'Дружеството плаща лихва върху вноската (чл. 134, ал. 3)' }))),
    lihvaWrap,
    field('Срок / условия за връщане', f.srok_vrashtane)
  );
  if (existing && existing.id) { const _ab = auditBlock(existing); if (_ab) body.appendChild(_ab); }

  const save = async () => {
    // Неразбрана дата не тръгва празна — сървърът би сложил днешна (ПРОМТ-19).
    if (!guardDates(f.protocol_date)) return;
    const p = {
      company_id: v.company_id || state().activeCompany,
      pravna_forma: 'ЕООД',
      protocol_date: f.protocol_date.dataset.iso, grad: f.grad.value,
      sobstvenik_ime: f.sobstvenik_ime.value,
      sobstvenik_is_legal: isLegal.checked,
      sobstvenik_egn: isLegal.checked ? '' : f.sobstvenik_egn.value,
      sobstvenik_eik: isLegal.checked ? f.sobstvenik_eik.value : '',
      predstavlyavasht: isLegal.checked ? f.predstavlyavasht.value : '',
      obshta_suma: f.obshta_suma.value,
      srok_vnasyane: f.srok_vnasyane.dataset.iso, smetka: f.smetka.value,
      lihva_flag: lihvaFlag.checked, lihva_procent: lihvaFlag.checked ? (f.lihva_procent.value === '' ? null : Number(f.lihva_procent.value)) : null,
      srok_vrashtane: f.srok_vrashtane.value,
    };
    try {
      if (existing && existing.id) await api('/vnoski/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/vnoski', { method: 'POST', body: JSON.stringify(p) });
      toast('Записано.'); closeModal(); window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); }
  };

  const foot = el('div', { style: 'flex-wrap:wrap' });
  if (existing && existing.id) {
    // „Създай подобен": ново решение със същия собственик и условия — без
    // id/номер, с днешна дата и БЕЗ срока за внасяне (реквизит на старото
    // решение; валидацията иска нов). Маскирано ЕГН се допопълва сървърно.
    foot.appendChild(el('button', { class: 'btn sm ghost copy-btn', text: 'Създай подобен', title: 'Ново решение със същия собственик и условия', style: 'margin-right:auto', onClick: () => {
      closeModal();
      eoodForm({
        company_id: v.company_id,
        pravna_forma: 'ЕООД',
        grad: v.grad,
        sobstvenik_ime: v.sobstvenik_ime,
        sobstvenik_egn: v.sobstvenik_egn,
        sobstvenik_is_legal: v.sobstvenik_is_legal,
        sobstvenik_eik: v.sobstvenik_eik,
        predstavlyavasht: v.predstavlyavasht,
        obshta_suma: v.obshta_suma,
        smetka: v.smetka,
        lihva_flag: v.lihva_flag,
        lihva_procent: v.lihva_procent,
        srok_vrashtane: v.srok_vrashtane,
      }, company);
    } }));
    foot.appendChild(el('button', { class: 'btn sm ghost', text: 'PDF', onClick: () => window.open('/api/vnoski/' + existing.id + '/pdf', '_blank') }));
    if (isAdmin) foot.appendChild(el('button', { class: 'btn sm danger', text: 'Изтрий', onClick: async () => { if (!(await confirmDialog('Да изтрия ли това решение?', { okText: 'Изтрий', danger: true }))) return; try { await api('/vnoski/' + existing.id, { method: 'DELETE' }); toast('Изтрито.'); closeModal(); window.CRM.renderView($('#page')); } catch (e) { toast(e.message, true); } } }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', text: 'Запази', onClick: save, style: 'margin-left:8px' }));
  openModal({ title: (existing && existing.id) ? ('Решение за допълнителни вноски № ' + existing.number) : 'Ново решение за допълнителни вноски — ЕООД (чл. 134 ТЗ)', body, foot, wide: true });
}
